import { useEffect, useRef, useState } from 'react'
import type { PhoneStatus } from '../types'

interface Props {
  phoneStatus: PhoneStatus
  onCall: (number: string) => void
  disabled?: boolean
}

interface Key {
  digit: string
  letters?: string
}

const KEYS: Key[] = [
  { digit: '1' },
  { digit: '2', letters: 'ABC' },
  { digit: '3', letters: 'DEF' },
  { digit: '4', letters: 'GHI' },
  { digit: '5', letters: 'JKL' },
  { digit: '6', letters: 'MNO' },
  { digit: '7', letters: 'PQRS' },
  { digit: '8', letters: 'TUV' },
  { digit: '9', letters: 'WXYZ' },
  { digit: '*' },
  { digit: '0' },
  { digit: '#' },
]

// Sticky international prefix. Initial state, backspace won't delete it —
// operator dials foreign numbers by default and shouldn't have to think
// about the leading '+' on every call.
const PREFIX = '+'

const MAX_LENGTH = 20

/**
 * Native-style 3x4 numpad used on mobile instead of a text input. No system
 * keyboard — keys append to a local string, backspace trims the last char,
 * CALL forwards the raw string to `onCall`. Validation is light (≥3 digits)
 * because `*`/`#` are legitimate DTMF and the relay/backend accepts any
 * reasonable E.164-ish input.
 */
export function MobileNumPad({ phoneStatus, onCall, disabled = false }: Props) {
  const [number, setNumber] = useState(PREFIX)
  const rootRef = useRef<HTMLDivElement>(null)
  const isOnline = phoneStatus === 'online' && !disabled
  const digitCount = number.replace(/[^\d]/g, '').length
  const canCall = isOnline && digitCount >= 3
  const backspaceDisabled = number === PREFIX

  const handleKey = (d: string) => {
    if (disabled) return
    setNumber(n => (n.length >= MAX_LENGTH ? n : n + d))
  }
  const handleBackspace = () => {
    setNumber(n => (n === PREFIX ? n : n.slice(0, -1)))
  }
  const handleCall = () => {
    if (canCall) onCall(number)
  }

  useEffect(() => {
    const isHidden = () => !rootRef.current || rootRef.current.offsetParent === null
    const isEditableTarget = (t: EventTarget | null) =>
      t instanceof HTMLElement &&
      (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)

    const onKey = (e: KeyboardEvent) => {
      // offsetParent === null when an ancestor has display:none — UserCallPage
      // keeps MobileNumPad mounted across tabs and just toggles CSS visibility.
      if (isHidden()) return
      if (isEditableTarget(e.target)) return
      if (disabled) return

      // Inline state mutations — keeps useEffect deps explicit (no closure
      // over handleKey/handleBackspace/handleCall, which would trip
      // react-hooks/exhaustive-deps).
      if (/^[0-9*#]$/.test(e.key)) {
        setNumber(n => (n.length >= MAX_LENGTH ? n : n + e.key))
        e.preventDefault()
        return
      }
      if (e.key === 'Backspace') {
        setNumber(n => (n === PREFIX ? n : n.slice(0, -1)))
        e.preventDefault()
        return
      }
      if (e.key === 'Enter') {
        if (canCall) {
          onCall(number)
          e.preventDefault()
        }
        return
      }
    }

    // paste/copy events fire on Ctrl+V / Ctrl+C without needing the async
    // navigator.clipboard.* permission prompt. clipboardData is synchronous.
    // On Chrome these dispatch on document/window even without an editable
    // focus target.
    const onPaste = (e: ClipboardEvent) => {
      if (isHidden()) return
      if (isEditableTarget(e.target)) return
      if (disabled) return
      const text = e.clipboardData?.getData('text') ?? ''
      const filtered = text.replace(/[^\d*#+]/g, '')
      if (!filtered) return
      // slice AFTER prepending PREFIX, otherwise 20 raw digits + '+' = 21 chars
      const withPrefix = filtered.startsWith('+')
        ? filtered
        : PREFIX + filtered.replace(/^\+/, '')
      setNumber(withPrefix.slice(0, MAX_LENGTH))
      e.preventDefault()
    }
    const onCopy = (e: ClipboardEvent) => {
      if (isHidden()) return
      if (isEditableTarget(e.target)) return
      if (disabled) return
      if (number === PREFIX) return
      e.clipboardData?.setData('text/plain', number)
      e.preventDefault()
    }

    window.addEventListener('keydown', onKey)
    window.addEventListener('paste', onPaste)
    window.addEventListener('copy', onCopy)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('paste', onPaste)
      window.removeEventListener('copy', onCopy)
    }
  }, [disabled, canCall, number, onCall])

  return (
    <div ref={rootRef} className="w-full h-full flex flex-col items-center px-4 pt-4 pb-8">
      <div
        data-testid="numpad-display"
        className="h-12 flex items-center justify-center w-full"
      >
        <span className="font-mono text-3xl font-semibold tracking-wider text-gray-900 dark:text-gray-100 tabular-nums">
          {number}
        </span>
      </div>

      <div className="flex-1" />

      <div
        role="group"
        aria-label="Numpad"
        className="grid grid-cols-3 gap-3 w-full max-w-[280px]"
      >
        {KEYS.map(({ digit, letters }) => (
          <button
            key={digit}
            type="button"
            onClick={() => handleKey(digit)}
            disabled={disabled}
            aria-label={letters ? `${digit} ${letters}` : digit}
            className="w-16 h-16 mx-auto rounded-full bg-white dark:bg-gray-800 shadow-sm
              flex flex-col items-center justify-center
              border border-gray-100 dark:border-gray-700
              hover:bg-gray-50 dark:hover:bg-gray-700 active:scale-95
              disabled:opacity-40 disabled:cursor-not-allowed
              transition-transform"
          >
            <span className="text-2xl font-semibold text-gray-900 dark:text-gray-100 leading-none">
              {digit}
            </span>
            {letters && (
              <span className="mt-0.5 text-[9px] font-mono tracking-[0.2em] text-gray-500 dark:text-gray-400">
                {letters}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-4 mt-6">
        <button
          type="button"
          onClick={handleCall}
          disabled={!canCall}
          className="flex items-center justify-center gap-2
            rounded-full bg-green-600 hover:bg-green-700
            disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:text-gray-500
            text-white font-semibold text-sm tracking-wide
            px-10 py-3 shadow-md transition-colors"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
          </svg>
          {disabled ? 'Calling...' : 'CALL'}
        </button>

        <button
          type="button"
          onClick={handleBackspace}
          disabled={backspaceDisabled}
          aria-label="Стереть"
          className="w-12 h-12 rounded-full
            bg-red-400 hover:bg-red-500
            disabled:opacity-40 disabled:cursor-not-allowed
            text-white flex items-center justify-center shadow-md transition-colors"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 4H8l-7 8 7 8h13a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z" />
            <line x1="18" y1="9" x2="12" y2="15" />
            <line x1="12" y1="9" x2="18" y2="15" />
          </svg>
        </button>
      </div>

      {phoneStatus === 'offline' && !disabled && (
        <p className="text-xs text-gray-400 dark:text-gray-500">Телефон не в сети</p>
      )}
    </div>
  )
}
